import * as React from "react";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import {
  Pencil,
  Check,
  X,
  Loader2,
  Calendar as CalendarIcon,
  ChevronDown,
  UserX,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";
import { Badge } from "./badge";
import { UserAvatar } from "../icons/avatars/UserAvatar.jsx";

const DATE_FNS_LOCALES = {
  en: enUS,
  es,
  pt: ptBR,
};

function resolveDateFnsLocale(locale = "en") {
  const base = String(locale || "en").split("-")[0].toLowerCase();
  return DATE_FNS_LOCALES[base] || enUS;
}

function parseDateValue(val) {
  if (!val) return undefined;
  if (val instanceof Date && !isNaN(val.getTime())) return val;
  const str = String(val);
  // Parse YYYY-MM-DD in local time so timezone shifts don't subtract a day
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? undefined : parsed;
}

function resolveBadgeVariant(val, getBadgeVariant, badgeVariantMap) {
  if (val === null || val === undefined || val === "") return undefined;
  const strVal = String(val);
  if (typeof getBadgeVariant === "function") {
    return getBadgeVariant(strVal);
  }
  if (badgeVariantMap && typeof badgeVariantMap === "object") {
    return badgeVariantMap[strVal];
  }
  return undefined;
}

function normalizeOptions(
  options = [],
  {
    enumObject,
    formatEnumLabel,
    getBadgeVariant,
    badgeVariantMap,
    yesLabel = "Yes",
    noLabel = "No",
    type,
  } = {}
) {
  if (type === "boolean" && (!options || options.length === 0)) {
    return [
      { value: "true", label: yesLabel },
      { value: "false", label: noLabel },
    ];
  }

  let sourceOptions = options;
  if ((!sourceOptions || sourceOptions.length === 0) && enumObject) {
    const rawValues = Array.isArray(enumObject)
      ? enumObject
      : typeof enumObject === "object"
        ? Object.values(enumObject)
        : [];
    const uniqueValues = Array.from(
      new Set(rawValues.filter((v) => v !== null && v !== undefined).map(String))
    );
    sourceOptions = uniqueValues.map((strVal) => ({
      value: strVal,
      label: formatEnumLabel ? formatEnumLabel(strVal) : strVal,
    }));
  }

  return (sourceOptions || []).map((opt) => {
    if (typeof opt === "object" && opt !== null) {
      const strVal = String(opt.value);
      return {
        value: strVal,
        label:
          opt.label ??
          (formatEnumLabel ? formatEnumLabel(strVal) : strVal),
        icon: opt.icon,
        avatar: opt.avatar || opt.avatarUrl || opt.pictureUrl,
        email: opt.email,
        userId: opt.userId,
        isUnassigned: Boolean(opt.isUnassigned),
        badgeVariant:
          opt.badgeVariant ??
          resolveBadgeVariant(strVal, getBadgeVariant, badgeVariantMap),
      };
    }
    const strVal = String(opt);
    return {
      value: strVal,
      label: formatEnumLabel ? formatEnumLabel(strVal) : strVal,
      badgeVariant: resolveBadgeVariant(
        strVal,
        getBadgeVariant,
        badgeVariantMap
      ),
    };
  });
}

function renderOptionContent(opt, type) {
  if (!opt) return null;
  if (opt.badgeVariant) {
    return (
      <Badge variant={opt.badgeVariant} className="text-overline-size leading-4">
        {opt.label}
      </Badge>
    );
  }
  const showAvatar =
    !opt.isUnassigned &&
    (type === "assignee" ||
      Boolean(opt.avatar || opt.email || opt.userId));

  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      {opt.isUnassigned ? (
        <span className="inline-flex size-4 items-center justify-center rounded-full border border-dashed border-muted-foreground/50 text-muted-foreground shrink-0">
          <UserX className="size-2.5" />
        </span>
      ) : showAvatar ? (
        <UserAvatar
          src={opt.avatar}
          name={opt.label}
          email={opt.email}
          userId={opt.userId || opt.value}
          size="xs"
        />
      ) : opt.icon ? (
        <opt.icon className="size-3.5 text-muted-foreground shrink-0" />
      ) : null}
      <span className="truncate">{opt.label}</span>
    </span>
  );
}

function formatDisplayValue(
  val,
  type,
  {
    currencySymbol = "$",
    normalizedOptions = [],
    formatEnumLabel,
    locale = "en",
  } = {}
) {
  if (val === null || val === undefined || val === "") return "";

  if (
    type === "select" ||
    type === "enum" ||
    type === "assignee" ||
    type === "boolean" ||
    normalizedOptions.length > 0
  ) {
    const match = normalizedOptions.find((o) => o.value === String(val));
    if (match) return match.label;
    if (formatEnumLabel) return formatEnumLabel(String(val));
    return String(val);
  }

  if (type === "date") {
    const d = parseDateValue(val);
    if (!d) return String(val);
    return d.toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  if (type === "currency") {
    const num = Number(val);
    if (isNaN(num)) return String(val);
    const formatted = num.toLocaleString(locale);
    return `${currencySymbol}${formatted}`;
  }

  if (type === "number") {
    const num = Number(val);
    return isNaN(num) ? String(val) : num.toLocaleString(locale);
  }

  return String(val);
}

const EditableCell = React.forwardRef(
  (
    {
      value,
      onSave,
      onCommit,
      type = "text",
      options = [],
      enumObject,
      formatEnumLabel,
      getBadgeVariant,
      badgeVariantMap,
      trigger = "click",
      currencySymbol = "$",
      placeholder = "—",
      disabled = false,
      validate,
      commitOnBlur = true,
      locale = "en",
      saveTitle = "Save (Enter)",
      cancelTitle = "Cancel (Esc)",
      invalidValueText = "Invalid value",
      saveErrorText = "Failed to save",
      selectPlaceholder = "Select...",
      yesLabel = "Yes",
      noLabel = "No",
      className,
      displayFormatter,
      renderDisplay,
      ...props
    },
    ref
  ) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [draftValue, setDraftValue] = React.useState(value ?? "");
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState(null);
    const inputRef = React.useRef(null);

    const normalizedOptions = React.useMemo(
      () =>
        normalizeOptions(options, {
          enumObject,
          formatEnumLabel,
          getBadgeVariant,
          badgeVariantMap,
          yesLabel,
          noLabel,
          type,
        }),
      [
        options,
        enumObject,
        formatEnumLabel,
        getBadgeVariant,
        badgeVariantMap,
        yesLabel,
        noLabel,
        type,
      ]
    );

    const isSelectMode =
      type === "select" ||
      type === "enum" ||
      type === "assignee" ||
      type === "boolean" ||
      Boolean(enumObject) ||
      normalizedOptions.length > 0;
    const isDateMode = type === "date";

    const selectedOption = React.useMemo(
      () =>
        isSelectMode
          ? normalizedOptions.find((o) => o.value === String(value ?? ""))
          : undefined,
      [isSelectMode, normalizedOptions, value]
    );

    // Keep draft value in sync when value changes externally
    React.useEffect(() => {
      if (!isEditing) {
        setDraftValue(value ?? "");
      }
    }, [value, isEditing]);

    // Select text on editing start for text/number inputs
    React.useEffect(() => {
      if (isEditing && !isSelectMode && !isDateMode && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing, isSelectMode, isDateMode]);

    const handleStartEditing = (e) => {
      e?.stopPropagation?.();
      if (disabled || isSaving) return;
      setError(null);
      setDraftValue(value ?? "");
      setIsEditing(true);
    };

    const commitChange = async (valToCommit) => {
      setError(null);

      // Validate
      if (validate) {
        const validationResult = validate(valToCommit);
        if (validationResult === false || typeof validationResult === "string") {
          setError(
            typeof validationResult === "string"
              ? validationResult
              : invalidValueText
          );
          return false;
        }
      }

      // Check if unchanged
      if (String(valToCommit ?? "") === String(value ?? "")) {
        setIsEditing(false);
        return true;
      }

      const saveHandler = onSave || onCommit;
      if (!saveHandler) {
        setIsEditing(false);
        return true;
      }

      try {
        setIsSaving(true);
        let parsed = valToCommit;
        if (type === "number" || type === "currency") {
          parsed = Number(String(valToCommit).replace(/[^0-9.-]+/g, ""));
        } else if (type === "boolean") {
          parsed = valToCommit === true || valToCommit === "true";
        }

        const result = await saveHandler(parsed);
        if (result === false) {
          setError(saveErrorText);
          return false;
        }

        setIsEditing(false);
        return true;
      } catch (err) {
        setError(err?.message || saveErrorText);
        return false;
      } finally {
        setIsSaving(false);
      }
    };

    const handleKeyDown = async (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (isSaving) return;
        await commitChange(draftValue);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setError(null);
        setDraftValue(value ?? "");
        setIsEditing(false);
      }
    };

    const handleBlur = async () => {
      if (!isEditing || isSaving || isSelectMode || isDateMode) return;
      if (commitOnBlur) {
        await commitChange(draftValue);
      } else {
        setError(null);
        setDraftValue(value ?? "");
        setIsEditing(false);
      }
    };

    const formattedDisplay = displayFormatter
      ? displayFormatter(value)
      : formatDisplayValue(value, type, {
          currencySymbol,
          normalizedOptions,
          formatEnumLabel,
          locale,
        });

    const resolvedDisplayBadgeVariant =
      selectedOption?.badgeVariant ??
      resolveBadgeVariant(value, getBadgeVariant, badgeVariantMap);

    // 1. Select / Enum / Assignee / Boolean Mode
    if (isSelectMode && isEditing) {
      return (
        <div
          ref={ref}
          data-testid="editable-cell-select"
          className={cn("relative flex items-center gap-1 min-w-[130px]", className)}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          <Select
            open={isEditing}
            onOpenChange={(open) => {
              if (!open && !isSaving) setIsEditing(false);
            }}
            value={String(draftValue ?? "")}
            onValueChange={(nextVal) => {
              setDraftValue(nextVal);
              commitChange(nextVal);
            }}
            disabled={isSaving}
          >
            <SelectTrigger className="h-8 min-h-[32px] px-2.5 py-1 text-xs rounded-lg border-primary ring-2 ring-primary/20 bg-background">
              <SelectValue placeholder={selectPlaceholder} />
            </SelectTrigger>
            <SelectContent onClick={(e) => e.stopPropagation()}>
              {normalizedOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {renderOptionContent(opt, type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isSaving && (
            <Loader2 className="size-3.5 animate-spin text-primary shrink-0" />
          )}
          {error && (
            <div className="absolute top-full left-0 z-20 mt-1 rounded bg-destructive/15 border border-destructive/40 px-1.5 py-0.5 text-overline-size text-destructive shadow-md">
              {error}
            </div>
          )}
        </div>
      );
    }

    // 2. Date Picker Mode
    if (isDateMode) {
      const selectedDate = parseDateValue(value);
      return (
        <div
          ref={ref}
          className={cn("relative inline-flex items-center", className)}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          <Popover
            open={isEditing}
            onOpenChange={(open) => {
              if (disabled || isSaving) return;
              setError(null);
              setIsEditing(open);
            }}
          >
            <PopoverTrigger asChild>
              <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                data-testid="editable-cell"
                className={cn(
                  "group flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors select-none text-left min-h-[32px]",
                  !disabled && "hover:bg-muted/60 cursor-pointer",
                  isEditing && "bg-muted/60 ring-2 ring-primary/20",
                  disabled && "cursor-default opacity-75"
                )}
              >
                <span
                  className={cn(
                    "truncate text-xs font-mono tabular-nums leading-none",
                    formattedDisplay ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {formattedDisplay || placeholder}
                </span>
                {isSaving ? (
                  <Loader2 className="size-3 animate-spin text-primary shrink-0" />
                ) : (
                  !disabled && (
                    <CalendarIcon className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  )
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0"
              align="start"
              onClick={(e) => e.stopPropagation()}
            >
              <Calendar
                mode="single"
                selected={selectedDate}
                defaultMonth={selectedDate}
                locale={resolveDateFnsLocale(locale)}
                onSelect={(date) => {
                  if (!date) return;
                  const isoDate = format(date, "yyyy-MM-dd");
                  commitChange(isoDate);
                }}
                autoFocus
              />
            </PopoverContent>
          </Popover>
          {error && (
            <div className="absolute top-full left-0 z-20 mt-1 rounded bg-destructive/15 border border-destructive/40 px-1.5 py-0.5 text-overline-size text-destructive shadow-md">
              {error}
            </div>
          )}
        </div>
      );
    }

    // 3. Text / Number / Currency Inline Input Mode
    if (isEditing) {
      return (
        <div
          ref={ref}
          data-testid="editable-cell-editing"
          className={cn("relative flex items-center gap-1 min-w-[90px]", className)}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type={type === "number" || type === "currency" ? "number" : "text"}
              value={draftValue}
              disabled={isSaving}
              onChange={(e) => {
                setError(null);
                setDraftValue(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className={cn(
                "h-8 w-full rounded-md bg-background border px-2 text-xs font-mono tabular-nums text-foreground outline-none transition-all",
                error
                  ? "border-destructive ring-2 ring-destructive/20"
                  : "border-primary ring-2 ring-primary/20"
              )}
            />
          </div>

          {/* Action indicators */}
          <div className="flex items-center gap-0.5 shrink-0">
            {isSaving ? (
              <Loader2 className="size-3.5 animate-spin text-primary" />
            ) : (
              <>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur before click
                    commitChange(draftValue);
                  }}
                  className="rounded p-1 text-success hover:bg-success/20 transition-colors cursor-pointer"
                  title={saveTitle}
                  aria-label={saveTitle}
                >
                  <Check className="size-3.5 stroke-[2.5]" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setError(null);
                    setDraftValue(value ?? "");
                    setIsEditing(false);
                  }}
                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/15 transition-colors cursor-pointer"
                  title={cancelTitle}
                  aria-label={cancelTitle}
                >
                  <X className="size-3.5" />
                </button>
              </>
            )}
          </div>

          {error && (
            <div className="absolute top-full left-0 z-20 mt-1 rounded bg-destructive/15 border border-destructive/40 px-1.5 py-0.5 text-overline-size text-destructive shadow-md">
              {error}
            </div>
          )}
        </div>
      );
    }

    // Default Display Mode
    const hasRichAssigneeOption =
      selectedOption &&
      (type === "assignee" ||
        selectedOption.isUnassigned ||
        Boolean(
          selectedOption.avatar ||
            selectedOption.email ||
            selectedOption.userId ||
            selectedOption.icon
        ));

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={disabled ? -1 : 0}
        data-testid="editable-cell"
        onClick={trigger === "click" ? handleStartEditing : undefined}
        onDoubleClick={trigger === "doubleClick" ? handleStartEditing : undefined}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleStartEditing(e);
          }
        }}
        className={cn(
          "group flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors select-none text-left min-h-[32px]",
          !disabled && "hover:bg-muted/60 cursor-pointer",
          disabled && "cursor-default opacity-75",
          className
        )}
        {...props}
      >
        {renderDisplay ? (
          renderDisplay(value, selectedOption)
        ) : resolvedDisplayBadgeVariant && formattedDisplay ? (
          <Badge
            variant={resolvedDisplayBadgeVariant}
            className="text-overline-size cursor-pointer"
          >
            {formattedDisplay}
          </Badge>
        ) : hasRichAssigneeOption ? (
          <span
            className={cn(
              "truncate text-xs leading-none",
              selectedOption?.isUnassigned
                ? "text-muted-foreground"
                : "text-foreground"
            )}
          >
            {renderOptionContent(selectedOption, type)}
          </span>
        ) : (
          <span
            className={cn(
              "truncate text-xs leading-none",
              (type === "number" || type === "currency") &&
                "font-mono tabular-nums",
              formattedDisplay ? "text-foreground" : "text-muted-foreground"
            )}
          >
            {formattedDisplay || placeholder}
          </span>
        )}
        {!disabled &&
          (isSelectMode ? (
            <ChevronDown className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          ) : (
            <Pencil className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          ))}
      </div>
    );
  }
);
EditableCell.displayName = "EditableCell";

export { EditableCell };
